import React from 'react';
import { Image, Text, View } from '@react-pdf/renderer';
import { pdfStyles } from './pdfStyles';
import {
  getSupplierItemCustomerTotal,
  getSupplierItemInternalCost,
  getSupplierItemInternalTotal,
  isPdfImageFile,
} from '../../utils/pdfHelpers';

const money = (value) => `${Number(value || 0).toLocaleString('da-DK')} DKK`;

function Cell({ children, width, header = false, last = false }) {
  return (
    <View style={[pdfStyles.cell, header && pdfStyles.headerCell, { width }, last && { borderRightWidth: 0 }]}>
      {typeof children === 'string' || typeof children === 'number' ? <Text>{children}</Text> : children}
    </View>
  );
}

function ItemFiles({ files = [] }) {
  if (files.length === 0) return null;
  return (
    <View style={pdfStyles.notes}>
      {files.map((file) => (
        isPdfImageFile(file) && file.objectUrl ? (
          <Image key={file.id || file.fileName} src={file.objectUrl} style={pdfStyles.image} />
        ) : (
          <Text key={file.id || file.fileName}>File: {file.fileName}</Text>
        )
      ))}
    </View>
  );
}

export default function OfferPDFItemsTable({ supplierSections = [], variant = 'internal' }) {
  const isInternal = variant === 'internal';
  const isSupplier = variant === 'supplier';
  const columns = isInternal
    ? [
        ['Supplier', '10%'],
        ['Description', '18%'],
        ['Qty', '6%'],
        ['Internal cost', '10%'],
        ['Shipping cost', '10%'],
        ['Internal total', '11%'],
        ['Margin %', '7%'],
        ['Discount %', '7%'],
        ['Customer total', '10%'],
        ['Branding / Delivery', '11%'],
      ]
    : isSupplier
      ? [
          ['Description', '30%'],
          ['Article / Item ID', '15%'],
          ['Qty', '8%'],
          ['Price', '13%'],
          ['Total', '14%'],
          ['Branding / Delivery', '20%'],
        ]
      : [
          ['Description', '38%'],
          ['Qty', '10%'],
          ['Price', '20%'],
          ['Branding / Delivery', '32%'],
        ];

  const rows = supplierSections.flatMap((section) => (
    (section.items || []).map((item) => ({ ...item, supplierName: section.displayName || section.name }))
  ));

  return (
    <View>
      <Text style={pdfStyles.sectionTitle}>Line items</Text>
      <View style={pdfStyles.table}>
        <View style={pdfStyles.row}>
          {columns.map(([title, width], index) => (
            <Cell key={title} width={width} header last={index === columns.length - 1}>{title}</Cell>
          ))}
        </View>
        {rows.map((item, index) => {
          const brandingDelivery = [
            item.productDetails,
            item.brandingDecoration,
            item.brandingDescription,
            item.deliveryRequirements,
            item.notes,
          ].filter(Boolean).join(' | ');
          const fileList = item.files || [];

          const lineTypeLabel = item.lineType && item.lineType !== 'Product' ? item.lineType : null;

          if (isSupplier) {
            return (
              <View key={`${item.supplierName}-${item.itemId || item.id || index}`} style={pdfStyles.row} wrap={false}>
                <Cell width="30%">
                  <Text>{item.description || item.productName}</Text>
                  {lineTypeLabel && <Text style={pdfStyles.notes}>{lineTypeLabel}</Text>}
                  <ItemFiles files={fileList} />
                </Cell>
                <Cell width="15%">{item.article || item.itemId || '-'}</Cell>
                <Cell width="8%">{Number(item.qty || item.quantity || 0)}</Cell>
                <Cell width="13%">{money(getSupplierItemInternalCost(item))}</Cell>
                <Cell width="14%">{money(getSupplierItemInternalTotal(item))}</Cell>
                <Cell width="20%" last>
                  <Text>{brandingDelivery || '-'}</Text>
                </Cell>
              </View>
            );
          }

          return (
            <View key={`${item.supplierName}-${item.itemId || item.id || index}`} style={pdfStyles.row} wrap={false}>
              {isInternal && <Cell width="10%">{item.supplierName}</Cell>}
              <Cell width={isInternal ? '18%' : '38%'}>
                <Text>{item.description || item.productName}</Text>
                {lineTypeLabel && <Text style={pdfStyles.notes}>{lineTypeLabel}</Text>}
                {item.article && <Text style={pdfStyles.notes}>Article: {item.article}</Text>}
                <ItemFiles files={fileList} />
              </Cell>
              <Cell width={isInternal ? '6%' : '10%'}>{Number(item.qty || item.quantity || 0)}</Cell>
              {isInternal && <Cell width="10%">{money(getSupplierItemInternalCost(item))}</Cell>}
              {isInternal && <Cell width="10%">{money(item.shippingCost ?? item.deliveryCost ?? 0)}</Cell>}
              {isInternal && <Cell width="11%">{money(getSupplierItemInternalTotal(item))}</Cell>}
              {isInternal && <Cell width="7%">{`${Number(item.marginPercent ?? item.margin ?? 0).toLocaleString('da-DK')}%`}</Cell>}
              {isInternal && <Cell width="7%">{`${Number(item.discount || 0).toLocaleString('da-DK')}%`}</Cell>}
              <Cell width={isInternal ? '10%' : '20%'}>{money(getSupplierItemCustomerTotal(item))}</Cell>
              <Cell width={isInternal ? '11%' : '32%'} last>
                <Text>{brandingDelivery || '-'}</Text>
              </Cell>
            </View>
          );
        })}
      </View>
    </View>
  );
}
